import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDirectory } from '../api/queries/directory';
import { ApiError } from '../api/client';
import { SortableTh } from '../components/SortableTh';
import { useClientSort } from '../hooks/useClientSort';
import './DirectoryPage.css';
import { TableScroll } from '../components/TableScroll';

type StaffSortKey = 'name' | 'type';

// Staff directory — individuals backed by a login account (`users` row). These
// are deliberately excluded from the Directory / Clients / Contractors / Vendors
// views and surface only here. Rows open the shared person page (/people/:id).
export function StaffPage(): JSX.Element {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const { data, isPending, error } = useDirectory({ q, role: 'staff' });
  const entries = data?.entries ?? [];
  const sort = useClientSort(entries, (e, key: StaffSortKey) => {
    switch (key) {
      case 'name': return e.display_name;
      case 'type': return 'staff';
      default: return '';
    }
  }, { key: 'name', dir: 'asc' });

  return (
    <section className="section-shell">
      <header className="section-header">
        <h2>Staff</h2>
        <div className="directory-header-actions">
          <span className="directory-count">{entries.length} shown</span>
        </div>
      </header>

      <div className="directory-filters">
        <input
          type="search"
          className="form-input"
          placeholder="Search staff…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {isPending ? (
        <p className="directory-status">Loading staff…</p>
      ) : error ? (
        <p className="directory-status directory-error">
          {error instanceof ApiError && error.status === 403
            ? "You don't have permission to view staff."
            : 'Failed to load staff. Try again in a moment.'}
        </p>
      ) : entries.length === 0 ? (
        <div className="section-body">
          <p>No staff{q ? ' match.' : ' yet.'}</p>
        </div>
      ) : (
        <div className="section-body section-body-flush">
          <TableScroll id="staff">
            <table className="directory-table">
              <thead>
                <tr>
                  <SortableTh column="name" label="Name" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
                  <SortableTh column="type" label="Type" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
                </tr>
              </thead>
              <tbody>
                {sort.sorted.map((e) => {
                  const href = e.person_id !== null ? `/people/${e.person_id}` : null;
                  return (
                    <tr
                      key={`staff-${e.person_id}`}
                      className={href ? 'directory-row directory-row-link' : 'directory-row'}
                      onClick={() => href && navigate(href)}
                    >
                      <td className="directory-name">{e.display_name}</td>
                      <td>
                        <span className="role-badge role-staff">Staff</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
              </TableScroll>
        </div>
      )}
    </section>
  );
}
